<template>
  <div class="space-y-16">
    <div class="col-span-2">
      <div v-if="slots.checkboxAll" class="py-12">
        <RevCheckbox
          :id="`${FORM_KEY}-all`"
          :checked="isAllChecked()"
          @click="handleToggleAll"
        >
          <template #label>
            <slot name="checkboxAll" v-bind="{ state }" />
          </template>
        </RevCheckbox>
      </div>
      <RevAccordionList
        class="box-border space-y-12 border-b-0 pt-12"
        :has-external-borders="true"
      >
        <RevAccordionItem
          v-for="(option, i) in availableOptions"
          :key="i"
          class="!p-16"
          :collapsed="hasContent(option) ? 'auto' : 'collapsed'"
          :wrapper-attrs="{
            class:
              'bg-surface-default-mid border-static-default-mid rounded-md overflow-hidden !border',
          }"
        >
          <template v-if="!hasContent(option)" #image></template>
          <template #title>
            <div class="inline-flex" @click.stop>
              <template v-if="hasChildren(option)">
                <RevCheckbox
                  :id="`${FORM_KEY}-option-${i}`"
                  :checked="isChecked(option)"
                  :disabled="isDisabled(option)"
                  @click="handleToggleOption(option)"
                >
                  <template #label>{{ option.label }}</template>
                </RevCheckbox>
              </template>
              <template v-if="!hasChildren(option)">
                <template v-if="option.value">
                  <RevCheckbox
                    :id="`${FORM_KEY}-option-${i}`"
                    v-model="model"
                    :checked="
                      option.appearsChecked ? [option.value] : undefined
                    "
                    :disabled="isDisabled(option)"
                    :value="option.value"
                  >
                    <template #label>{{ option.label }}</template>
                  </RevCheckbox>
                </template>
                <template v-if="!option.value">
                  {{ option.label }}
                </template>
              </template>
            </div>
          </template>
          <template v-if="hasContent(option)" #body>
            <div class="border-static-default-mid -mx-8 border-t pt-8">
              <template v-if="slots.item">
                <slot name="item" v-bind="{ item: option }" />
              </template>
              <template v-if="!slots.item">
                <p v-if="option.description">{{ option.description }}</p>
                <div
                  v-if="hasChildren(option)"
                  class="ml-48 space-y-16 sm:grid sm:grid-cols-2 sm:gap-16 sm:space-y-0 md:grid-cols-4"
                >
                  <RevCheckbox
                    v-for="{
                      value,
                      label,
                      disabled,
                      appearsChecked,
                    } in option.children"
                    :id="`${FORM_KEY}-option-${value}`"
                    :key="value"
                    v-model="model"
                    :checked="appearsChecked ? [value] : undefined"
                    :disabled="disabled || isDisabled(option)"
                    :value
                  >
                    <template #label>{{ label }}</template>
                  </RevCheckbox>
                </div>
              </template>
            </div>
          </template>
        </RevAccordionItem>
      </RevAccordionList>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { VNode } from 'vue'

import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevAccordionList } from '@ds/components/AccordionList'
import { RevCheckbox } from '@ds/components/Checkbox'

import { distinctArray } from '../../Shipping.utils'

import type {
  TreeSelectModelSetter,
  TreeSelectOption,
  TreeSelectOptionValue,
  TreeSelectState,
} from './TreeSelect.types'
import { useTreeSelect } from './composables/useTreeSelect'

const FORM_KEY = crypto.randomUUID()

const props = defineProps<{
  availableOptions: TreeSelectOption[]
}>()

const slots = defineSlots<{
  checkboxAll?(props: { state?: TreeSelectState }): VNode
  item?(props: { item: TreeSelectOption }): VNode
}>()

const model = defineModel<TreeSelectOptionValue[]>({
  default: () => [],
})

function updateModel({ add = [], remove = [] }: TreeSelectModelSetter) {
  model.value = distinctArray(
    model.value.filter((value) => !remove.includes(value)).concat(add),
  )
}

const {
  state,
  isAllChecked,
  isChecked,
  isDisabled,
  hasChildren,
  hasContent,
  toggleOption,
  toggleAll,
} = useTreeSelect(model, props.availableOptions)

function handleToggleOption(option: TreeSelectOption) {
  updateModel(toggleOption(option))
}

function handleToggleAll() {
  updateModel(toggleAll())
}
</script>
